import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './IndustrySlider.css';

// Map industry names (from DB) to frontend static images
const INDUSTRY_IMAGES = {
  'Information Technology': '/images/industry/information-technology.png',
  'Automotive': '/images/industry/automotive.png',
  'Banking, Financial Services & Insurance (BFSI)': '/images/industry/bfsi.png',
  'NBFC & Fintech': '/images/industry/nbfc-fintech.png',
  'FMCG & Consumer Goods': '/images/industry/fmcg-consumer-goods.png',
  'Manufacturing & Industrial': '/images/industry/manufacturing-industrial.png',
  'Retail & E-Commerce': '/images/industry/retail-ecommerce.png',
  'Pharmaceutical & Healthcare': '/images/industry/pharmaceutical-healthcare.png',
  'Logistics & Supply Chain': '/images/industry/logistics-supply-chain.png',
  'Construction & Real Estate': '/images/industry/construction-real-estate.png',
  'Telecom & Telecommunications': '/images/industry/telecom-telecommunications.png',
  'Hospitality & Services': '/images/industry/hospitality-services.png',
};

const App = ({ url }) => {
  const apiUrl = url || process.env.REACT_APP_API_URL || 'http://localhost:4000';
  const [cardData, setCardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCards, setVisibleCards] = useState(4);
  const [index, setIndex] = useState(4);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const trackRef = useRef(null);

  // Fetch industries from DB
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/industry/list`);
        const data = await res.json();
        if (data?.success && Array.isArray(data.data)) {
          const cards = data.data.map((ind) => ({
            title: ind.name,
            description: ind.description || '',
            image: INDUSTRY_IMAGES[ind.name] || (ind.image ? (ind.image.startsWith('industry/') ? `/images/${ind.image}` : `${apiUrl}/images/${ind.image}`) : '/images/industry/information-technology.png'),
            alt: `${ind.name} industry`,
          }));
          setCardData(cards);
        }
      } catch (err) {
        console.error('Failed to fetch industries:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchIndustries();
  }, [apiUrl]);

  // Adjust visible cards based on window width
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setVisibleCards(1);
      else if (window.innerWidth < 768) setVisibleCards(2);
      else if (window.innerWidth < 1024) setVisibleCards(3);
      else setVisibleCards(4);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recalculate extended cards whenever visibleCards or cardData changes
  const totalCards = cardData.length;
  const extendedCards = totalCards > 0
    ? [
        ...cardData.slice(-visibleCards),
        ...cardData,
        ...cardData.slice(0, visibleCards),
      ]
    : [];

  useEffect(() => {
    setIndex(visibleCards);
  }, [visibleCards, totalCards]);

  const next = () => setIndex((prev) => prev + 1);
  const prev = () => setIndex((prev) => prev - 1);

  // Autoplay
  useEffect(() => {
    const interval = setInterval(next, 3000);
    return () => clearInterval(interval);
  }, []);

  // Handle circular reset
  useEffect(() => {
    if (index >= totalCards + visibleCards) {
      const id = setTimeout(() => {
        setTransitionEnabled(false);
        setIndex(visibleCards);
      }, 500);
      return () => clearTimeout(id);
    } else if (index <= 0) {
      const id = setTimeout(() => {
        setTransitionEnabled(false);
        setIndex(totalCards);
      }, 500);
      return () => clearTimeout(id);
    }
    setTransitionEnabled(true);
  }, [index, totalCards, visibleCards]);

  if (loading) {
    return (
      <div className="app-container">
        <div className="slider-container">
          <h1 className="slider-title">Industries we work with</h1>
          <div className="slider-loading">Loading industries...</div>
        </div>
      </div>
    );
  }

  if (totalCards === 0) {
    return null;
  }

  return (
    <div className="app-container">
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css"
      />
      <div className="slider-container">
        <h1 className="slider-title">Industries we work with</h1>
        <div className="slider-wrapper">
          <div
            ref={trackRef}
            className="slider-track"
            style={{
              transform: `translateX(-${index * (100 / visibleCards)}%)`,
              transition: transitionEnabled ? 'transform 0.5s ease-in-out' : 'none',
            }}
          >
            {extendedCards.map((card, idx) => (
              <div key={idx} className="card">
                <div className="card-content">
                  <img
                    src={card.image.startsWith('http') || card.image.startsWith('/') ? card.image : `images/${card.image}`}
                    alt={card.alt}
                    className="card-image"
                  />
                  <div className="card-body">
                    <h3 className="card-title">{card.title}</h3>
                    <p className="card-description">{card.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button onClick={prev} className="slider-button prev-button">
          <i className="fas fa-chevron-left"></i>
        </button>
        <button onClick={next} className="slider-button next-button">
          <i className="fas fa-chevron-right"></i>
        </button>
      </div>

      <div className="view-all-container">
        <Link to="/business#industries">
          <button type="button" className="view-all-button">View All Industries</button>
        </Link>
      </div>

    </div>
  );
};

export default App;
